import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

// Types for session management
export interface SessionType {
    EXERCISE: 'exercise';
    TESTING: 'testing';
    LEARNING: 'learning';
    FLASHCARD: 'flashcard';
    VIDEO_LEARNING: 'video_learning';
    SKILL_ASSESSMENT: 'skill_assessment';
    CONVERSATION: 'conversation';
    READING: 'reading';
    LISTENING: 'listening';
    WRITING: 'writing';
    SPEAKING: 'speaking';
    VOCABULARY: 'vocabulary';
    GRAMMAR: 'grammar';
    PRONUNCIATION: 'pronunciation';
    CUSTOM: 'custom';
}

export interface SessionStatus {
    ACTIVE: 'active';
    PAUSED: 'paused';
    COMPLETED: 'completed';
    ABANDONED: 'abandoned';
    EXPIRED: 'expired';
}

export interface Session {
    id: number;
    session_id: string;
    session_type: keyof SessionType;
    status: keyof SessionStatus;
    title: string;
    description: string;
    started_at: string;
    last_activity_at: string;
    paused_at?: string;
    completed_at?: string;
    estimated_duration?: string;
    actual_duration?: string;
    total_items: number;
    completed_items: number;
    correct_items: number;
    score: number;
    max_score: number;
    session_data: Record<string, any>;
    settings: Record<string, any>;
    user_agent: string;
    ip_address?: string;
    device_info: Record<string, any>;
    xp_earned: number;
    achievements_unlocked: string[];
    content_type?: number;
    object_id?: number;
    content_type_name?: string;
    content_object_str?: string;
    progress_percentage: number;
    accuracy_percentage: number;
    is_active: boolean;
    is_completed: boolean;
    activities?: SessionActivity[];
    bookmarks?: SessionBookmark[];
    analytics?: SessionAnalytics;
}

export interface SessionActivity {
    id: number;
    activity_type: string;
    sequence_number: number;
    question_id: string;
    user_response: Record<string, any>;
    correct_answer: Record<string, any>;
    is_correct?: boolean;
    started_at: string;
    completed_at?: string;
    time_spent?: string;
    points_earned: number;
    max_points: number;
    metadata: Record<string, any>;
}

export interface SessionBookmark {
    id: number;
    title: string;
    description: string;
    activity_sequence: number;
    timestamp: string;
    bookmark_data: Record<string, any>;
    created_at: string;
}

export interface SessionAnalytics {
    id: number;
    response_times: number[];
    difficulty_progression: any[];
    strong_areas: any[];
    weak_areas: any[];
    interaction_patterns: Record<string, any>;
    next_recommendations: any[];
    ai_feedback: string;
    created_at: string;
    updated_at: string;
}

export interface SessionTemplate {
    id: number;
    name: string;
    session_type: keyof SessionType;
    description: string;
    default_duration?: string;
    default_total_items: number;
    template_settings: Record<string, any>;
    scoring_method: string;
    max_score: number;
    base_xp_reward: number;
    bonus_xp_rules: Record<string, any>;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface SessionStats {
    total_sessions: number;
    completed_sessions: number;
    active_sessions: number;
    total_time_spent: string;
    average_score: number;
    total_xp_earned: number;
    sessions_by_type: Record<string, number>;
    recent_sessions: Session[];
}

export interface CreateSessionData {
    session_id?: string;
    session_type: keyof SessionType;
    title: string;
    description?: string;
    estimated_duration?: string;
    total_items?: number;
    max_score?: number;
    session_data?: Record<string, any>;
    settings?: Record<string, any>;
    content_type?: number;
    object_id?: number;
    content_type_name?: string;
}

export interface UpdateSessionData {
    status?: keyof SessionStatus;
    title?: string;
    description?: string;
    completed_items?: number;
    correct_items?: number;
    score?: number;
    session_data?: Record<string, any>;
    settings?: Record<string, any>;
    xp_earned?: number;
    achievements_unlocked?: string[];
}

export interface CreateActivityData {
    activity_type: string;
    sequence_number: number;
    question_id?: string;
    user_response?: Record<string, any>;
    correct_answer?: Record<string, any>;
    is_correct?: boolean;
    completed_at?: string;
    time_spent?: string;
    points_earned?: number;
    max_points?: number;
    metadata?: Record<string, any>;
}

// Helper function to get auth headers
const getAuthHeaders = () => {
    const token = localStorage.getItem('access');
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// Session Management API Service
const sessionService = {
    // Session Management
    async getSessions(): Promise<Session[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async getSession(sessionId: number): Promise<Session> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async createSession(data: CreateSessionData): Promise<Session> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async updateSession(sessionId: number, data: UpdateSessionData): Promise<Session> {
        const response = await axios.patch(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async deleteSession(sessionId: number): Promise<void> {
        await axios.delete(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/`, {
            headers: getAuthHeaders(),
        });
    },

    // Session Control
    async pauseSession(sessionId: number): Promise<{ status: string }> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/pause/`, {}, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async resumeSession(sessionId: number): Promise<{ status: string }> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/resume/`, {}, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async completeSession(sessionId: number, finalScore?: number): Promise<Session> {
        const data = finalScore ? { final_score: finalScore } : {};
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/complete/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async abandonSession(sessionId: number): Promise<{ status: string }> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/abandon/`, {}, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Active Sessions
    async getActiveSessions(): Promise<Session[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/active/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Filters
    async getSessionsByType(sessionType: keyof SessionType): Promise<Session[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/by_type/`, {
            params: { type: sessionType },
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async getSessionsByDateRange(startDate: string, endDate: string): Promise<Session[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/by_date_range/`, {
            params: { start_date: startDate, end_date: endDate },
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Stats
    async getSessionStats(): Promise<SessionStats> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/stats/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Activities
    async getSessionActivities(sessionId: number): Promise<SessionActivity[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/activities/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async createSessionActivity(sessionId: number, data: CreateActivityData): Promise<SessionActivity> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/activities/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async updateSessionActivity(sessionId: number, activityId: number, data: Partial<CreateActivityData>): Promise<SessionActivity> {
        const response = await axios.patch(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/activities/${activityId}/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Bookmarks
    async getSessionBookmarks(sessionId: number): Promise<SessionBookmark[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/bookmarks/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async createSessionBookmark(sessionId: number, data: {
        title: string;
        description?: string;
        activity_sequence: number;
        bookmark_data?: Record<string, any>;
    }): Promise<SessionBookmark> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/sessions/${sessionId}/bookmarks/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Templates
    async getSessionTemplates(): Promise<SessionTemplate[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/templates/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async createSessionFromTemplate(templateId: number, data: {
        title?: string;
        session_data?: Record<string, any>;
        content_type?: number;
        object_id?: number;
    }): Promise<Session> {
        const response = await axios.post(`${API_BASE_URL}/sessions/v1/templates/${templateId}/create_session/`, data, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    // Session Analytics
    async getSessionAnalytics(): Promise<SessionAnalytics[]> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/analytics/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },

    async generateInsights(): Promise<any> {
        const response = await axios.get(`${API_BASE_URL}/sessions/v1/analytics/generate_insights/`, {
            headers: getAuthHeaders(),
        });
        return response.data;
    },
};

export default sessionService; 